import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Modal, Timeline, Empty, Avatar } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';

const WorkflowCC: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [ccList, setCcList] = useState<workflowService.WorkflowCC[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [currentHistory, setCurrentHistory] = useState<workflowService.WorkflowHistory[]>([]);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getMyCCs({ page, pageSize: 10 });
      if (res.data) {
        setCcList(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await workflowService.markCCRead(id);
      message.success('已标记为已读');
      loadData();
    } catch {
      message.error('操作失败');
    }
  };

  const viewHistory = async (instanceId: number) => {
    try {
      const res = await workflowService.getInstanceHistory(instanceId);
      if (res.data) {
        setCurrentHistory(res.data as workflowService.WorkflowHistory[]);
        setHistoryModalVisible(true);
      }
    } catch {}
  };

  const columns: ColumnsType<workflowService.WorkflowCC> = [
    {
      title: '流程标题',
      dataIndex: ['instance', 'title'],
      key: 'title',
      render: (_, record: any) => record.instance?.title || '-',
    },
    {
      title: '发起人',
      dataIndex: ['instance', 'initiatorName'],
      key: 'initiator',
      render: (_, record: any) => record.instance?.initiatorName || '-',
    },
    { title: '抄送节点', dataIndex: 'nodeId', key: 'nodeId' },
    {
      title: '抄送时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '状态',
      dataIndex: 'isRead',
      key: 'isRead',
      render: (isRead: boolean) => <Tag color={isRead ? 'default' : 'processing'}>{isRead ? '已读' : '未读'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {!record.isRead && (
            <Button size="small" type="primary" onClick={() => handleMarkRead(record.id)}>标记已读</Button>
          )}
          <Button size="small" icon={<HistoryOutlined />} onClick={() => viewHistory(record.instanceId)}>历史</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="抄送给我">
        <Table
          columns={columns}
          dataSource={ccList}
          rowKey="id"
          loading={loading}
          pagination={{ current: page, total, pageSize: 10, showTotal: (t) => `共 ${t} 条`, onChange: setPage }}
        />
      </Card>

      <Modal title="审批历史" open={historyModalVisible} onCancel={() => setHistoryModalVisible(false)} footer={null} width={700}>
        {currentHistory.length > 0 ? (
          <Timeline
            items={currentHistory.map((h) => ({
              color: h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue',
              children: (
                <div>
                  <Space><Avatar size="small">{h.operatorName?.charAt(0)}</Avatar><strong>{h.operatorName}</strong><Tag>{h.nodeName}</Tag></Space>
                  <div style={{ marginTop: 8, marginLeft: 32 }}>
                    <Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>
                      {h.action === 'submit' && '发起流程'}{h.action === 'approve' && '审批通过'}{h.action === 'reject' && '审批拒绝'}
                    </Tag>
                    {h.comment && <div style={{ marginTop: 4, color: '#666', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>{h.comment}</div>}
                  </div>
                </div>
              ),
            }))}
          />
        ) : <Empty description="暂无历史记录" />}
      </Modal>
    </div>
  );
};

export default WorkflowCC;
